import { useNavigate } from 'react-router-dom';
import { Badge, useFormatShortDate } from '@insio/ui-toolkit';
import { plainDateFrom, nowPlainDate } from '@insio/dates';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { navigation } from '@/navigation';
import {
  isValidInvoiceCardId,
  toInvoiceCardVariant,
  type InvoiceCardPartData,
  type InvoiceCardVariant,
  type InvoiceRowData
} from './invoice-card.types';
import { useInvoiceCardStatus } from './use-invoice-card-status';
import { paymentStatusColor } from '../badge-colors';
import { CardActions, CardButton, ResultCard, ResultCardHeader } from '../ResultCard';
import { useFormatCurrency } from '@/hooks/use-format-currency';

interface Props {
  part: InvoiceCardPartData;
}

/** Formats an ISO date string with the user's short-date locale; falls back to the raw value. */
export function useCardDate(): (date: string | null | undefined) => string | null {
  const formatShortDate = useFormatShortDate();
  return (date: string | null | undefined): string | null => {
    if (!date) return null;
    try {
      return formatShortDate(plainDateFrom(date.slice(0, 10)));
    } catch {
      return date;
    }
  };
}

/** Whole days from a due date until today — positive once the date has passed. */
export function daysOverdue(dueDate: string | null | undefined): number | null {
  if (!dueDate) return null;
  try {
    return nowPlainDate().since(plainDateFrom(dueDate.slice(0, 10))).days;
  } catch {
    return null;
  }
}

/** Error/warning counts of one invoice — the headline of the validation variant. */
export function ValidationCounts({ row }: { row: InvoiceRowData }) {
  const t = useFeatureTranslation('aiChat');
  const errors = row.errorCount ?? 0;
  const warnings = row.warningCount ?? 0;

  if (errors === 0 && warnings === 0) return null;

  return (
    <div className='flex items-center gap-xs'>
      {errors > 0 && (
        <Badge color='error' size='sm'>
          {t('invoiceCard.errorCount', { count: errors })}
        </Badge>
      )}
      {warnings > 0 && (
        <Badge color='warning' size='sm'>
          {t('invoiceCard.warningCount', { count: warnings })}
        </Badge>
      )}
    </div>
  );
}

/** The badge each variant leads with — what the user asked about comes first. */
function HeadlineBadge({ row, variant }: { row: InvoiceRowData; variant: InvoiceCardVariant }) {
  const tPayment = useEnumTranslation('paymentStatus');
  const t = useFeatureTranslation('aiChat');
  const statusOf = useInvoiceCardStatus();
  const overdue = daysOverdue(row.dueDate);

  if (variant === 'validation') {
    return <ValidationCounts row={row} />;
  }

  if (variant === 'payment') {
    if (overdue !== null && overdue > 0) {
      return (
        <Badge color='error' size='sm'>
          {t('invoiceCard.overdueDays', { count: overdue })}
        </Badge>
      );
    }
    return row.paymentStatus ? (
      <Badge color={paymentStatusColor(row.paymentStatus)} size='sm'>
        {tPayment(row.paymentStatus)}
      </Badge>
    ) : null;
  }

  // lookup and approval both lead with the invoice status.
  const status = statusOf(row);
  return status ? (
    <Badge color={status.color} size='sm'>
      {status.label}
    </Badge>
  ) : null;
}

export function InvoiceCardPart({ part }: Props) {
  const navigate = useNavigate();
  const t = useFeatureTranslation('aiChat');
  const formatDate = useCardDate();
  const formatAmount = useFormatCurrency();
  const variant = toInvoiceCardVariant(part.variant);

  const amount = formatAmount(part.amount, part.currency);
  // Payment questions are about the due date; everything else about the issue date.
  const dateLabel = variant === 'payment' ? t('invoiceCard.due') : null;
  const shownDate = formatDate(variant === 'payment' ? part.dueDate : part.date);
  const statusOf = useInvoiceCardStatus();
  // The validation variant leads with its counts, so the status still needs a place.
  const secondaryStatus = variant === 'validation' ? statusOf(part) : null;

  return (
    <ResultCard>
      <ResultCardHeader title={part.invoiceNumber ?? '—'}>
        <HeadlineBadge row={part} variant={variant} />
      </ResultCardHeader>

      <div className='flex flex-col gap-2xs text-foreground-muted text-sm'>
        {part.supplier && <span>{part.supplier}</span>}
        <div className='flex items-center gap-sm'>
          {amount && (
            <span
              className={
                // Money is not the answer to a validation question — keep it present, not loud.
                variant === 'validation'
                  ? 'text-foreground-muted'
                  : 'font-medium text-foreground-base'
              }
            >
              {amount}
            </span>
          )}
          {shownDate && <span>{dateLabel ? `${dateLabel} ${shownDate}` : shownDate}</span>}
          {secondaryStatus && <span>{secondaryStatus.label}</span>}
        </div>
      </div>

      {isValidInvoiceCardId(part.id) && (
        <CardActions>
          <CardButton onClick={() => navigate(navigation.invoiceDetail(part.id))}>
            {t('openInvoice')}
          </CardButton>
        </CardActions>
      )}
    </ResultCard>
  );
}
